import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button, Stack, Text, Alert, Loader, Center, Paper, Title } from '@mantine/core';
import { IconCheck, IconX, IconMail, IconAlertCircle } from '@tabler/icons-react';
import { HttpCodes, Logo, AppName } from '../../lib/data';
import { Endpoints } from '../../lib/endpoints';
import { PerformRequest } from '../../lib/usePerformRequest';
import { NonPaginatedResponse } from '../../types';
import './styles.scss';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');

    if (!emailParam || !tokenParam) {
      setError('Invalid verification link. Please check your email and try again.');
      setIsLoading(false);
      return;
    }

    setEmail(emailParam);
    setToken(tokenParam);

    // Automatically verify on mount
    verifyEmail(emailParam, tokenParam);
  }, [searchParams]);

  const verifyEmail = async (emailAddress: string, verificationToken: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const baseRoute = Endpoints.GetVerifyEmail().route;
      const queryParams = new URLSearchParams({
        email: emailAddress,
        token: verificationToken,
      });
      const fullRoute = `${baseRoute}?${queryParams.toString()}`;

      const response = await PerformRequest<NonPaginatedResponse<any>>({
        method: Endpoints.GetVerifyEmail().method,
        route: fullRoute,
        doNotUseAuthorization: true,
      });

      if (response) {
        if (response.status === HttpCodes.Success) {
          setIsVerified(true);
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/login', { 
              state: { message: 'Email verified successfully! You can now log in.' } 
            });
          }, 3000);
        } else {
          setError(response.message || 'Verification failed. Please try again.');
        }
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during verification. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmail = () => {
    // TODO: Implement resend verification email functionality
    navigate('/login');
  };

  return (
    <div className="verify-email-page">
      <div className="verify-email-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="verify-email-content"
        >
          <Paper shadow="md" p="xl" radius="md" withBorder className="verify-email-paper">
            {isLoading ? (
              <Stack align="center" gap="lg">
                <Loader size="xl" color="green" />
                <Title order={3} ta="center">Verifying your email...</Title>
                <Text c="dimmed" ta="center" size="sm">
                  Please wait while we verify your email address.
                </Text>
              </Stack>
            ) : isVerified ? (
              <Stack align="center" gap="lg">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <div className="success-icon">
                    <IconCheck size={64} stroke={2.5} />
                  </div>
                </motion.div>
                <Title order={2} ta="center" c="green">
                  Email Verified Successfully!
                </Title>
                <Text ta="center" c="dimmed" size="md">
                  Your email address has been verified. You can now log in to your account.
                </Text>
                <Text ta="center" c="dimmed" size="sm">
                  Redirecting to login page...
                </Text>
                <Button
                  fullWidth
                  size="md"
                  onClick={() => navigate('/login')}
                  mt="md"
                >
                  Go to Login
                </Button>
              </Stack>
            ) : (
              <Stack align="center" gap="lg">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", stiffness: 200, damping: 15 }}
                >
                  <div className="error-icon">
                    <IconX size={64} stroke={2.5} />
                  </div>
                </motion.div>
                <Title order={2} ta="center" c="red">
                  Verification Failed
                </Title>
                <Alert
                  icon={<IconAlertCircle size={16} />}
                  title="Error"
                  color="red"
                  variant="light"
                >
                  {error || 'Unable to verify your email. The link may have expired or is invalid.'}
                </Alert>
                <Text ta="center" c="dimmed" size="sm">
                  Verification links expire after 24 hours. If your link has expired, please request a new one.
                </Text>
                <Stack gap="sm" w="100%">
                  <Button
                    fullWidth
                    size="md"
                    onClick={() => navigate('/login')}
                    variant="filled"
                  >
                    Go to Login
                  </Button>
                  <Button
                    fullWidth
                    size="md"
                    variant="outline"
                    onClick={handleResendEmail}
                    leftSection={<IconMail size={16} />}
                  >
                    Request New Verification Email
                  </Button>
                </Stack>
              </Stack>
            )}
          </Paper>
        </motion.div>
      </div>
    </div>
  );
}

